import React, { useState } from 'react';
import {
  Show,
  TopToolbar,
  EditButton,
  ListButton,
  useShowContext,
  useRedirect,
  useGetOne,
  useNotify,
  useRefresh,
} from 'react-admin';
import { BackButton } from '../../components/BackButton';
import { NavigationBreadcrumbs, BreadcrumbItem } from '../../components/NavigationBreadcrumbs';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Grid,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  TextField as MuiTextField,
  FormControl,
} from '@mui/material';
import {
  Edit as EditIcon,
  Email as EmailIcon,
  Print as PrintIcon,
  Visibility as PreviewIcon,
  CheckCircle as ApproveIcon,
  Receipt as InvoiceIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  Business as BusinessIcon,
  Send as SendIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { InvoiceGenerationWizard } from '../../components/InvoiceGenerationWizard';
import { useBrand } from '../../components/ServerBrandProvider';

interface Customer {
  id: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  email?: string;
  phone?: string;
}

interface LineItem {
  id?: string;
  name: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
}

interface EstimateRecord {
  id: string;
  billNumber: string;
  estimateNumber?: string;
  title?: string;
  customerId: string;
  customer?: Customer;
  state: string;
  status?: 'draft' | 'sent' | 'approved' | 'rejected' | 'expired';
  isApproved: boolean;
  issueDate?: string;
  billDate: string;
  validUntil?: string;
  lineItems?: LineItem[];
  subtotal: number;
  salesTaxAmount: number;
  taxAmount?: number;
  totalAmount: number;
  taxRate?: number;
  notes?: string;
  terms?: string;
  createdAt: string;
}

interface EmailPreviewData {
  preview: boolean;
  subject: string;
  html: string;
  templateData: {
    billNumber: string;
    isEstimate: boolean;
    customer: {
      name: string;
      company?: string;
    };
    amount: number;
    formattedAmount: string;
    billDate: string;
    notes?: string;
    customMessage?: string;
  };
  template: string;
}

interface EmailFormData {
  toEmail: string;
  ccEmails: string[];
  subject: string;
  message: string;
  includePdf: boolean;
}

const ShowActions = () => (
  <TopToolbar>
    <BackButton 
      fallbackPath="/estimates" 
      variant="icon" 
      tooltip="Back to estimates list"
    />
    <ListButton />
    <EditButton />
  </TopToolbar>
);

const StatusChip = ({ record }: { record: EstimateRecord }) => {
  const getStatusColor = (state: string) => {
    switch (state.toLowerCase()) {
      case 'draft':
        return 'default';
      case 'sent':
        return 'info';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'expired':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Chip
      label={record.state?.toUpperCase() || 'UNKNOWN'}
      color={getStatusColor(record.state || '') as any}
      size="small"
    />
  );
};

// API Functions
const generateEmailPreview = async (
  billId: string,
  emailData: Partial<EmailFormData>
): Promise<EmailPreviewData> => {
  const accessToken = localStorage.getItem('accessToken');
  const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;

  const response = await fetch(`${baseUrl}/crm/bills/${billId}/email/preview`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      'x-tenant-code': localStorage.getItem('currentTenant')
        ? JSON.parse(localStorage.getItem('currentTenant')!).code
        : 'default',
    },
    body: JSON.stringify({
      ...emailData,
      includePdf: true,
      previewOnly: true,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to generate email preview');
  }

  return response.json();
};

const sendEstimateEmail = async (
  billId: string,
  emailData: EmailFormData
): Promise<{ sent: boolean; to: string; subject: string }> => {
  const accessToken = localStorage.getItem('accessToken');
  const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;

  const response = await fetch(`${baseUrl}/crm/bills/${billId}/email`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      'x-tenant-code': localStorage.getItem('currentTenant')
        ? JSON.parse(localStorage.getItem('currentTenant')!).code
        : 'default',
    },
    body: JSON.stringify({
      ...emailData,
      includePdf: true,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to send email');
  }

  return response.json();
};

const EstimateShowContent = () => {
  const { record } = useShowContext<EstimateRecord>();
  const redirect = useRedirect();
  const notify = useNotify();
  const refresh = useRefresh();
  const { brand } = useBrand();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState<EmailPreviewData | null>(null);
  const [isLoadingPreviewData, setIsLoadingPreviewData] = useState(false);
  const [printPreviewOpen, setPrintPreviewOpen] = useState(false);
  const [printPreviewData, setPrintPreviewData] = useState<EmailPreviewData | null>(null);
  const [isLoadingPrintPreview, setIsLoadingPrintPreview] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [invoiceWizardOpen, setInvoiceWizardOpen] = useState(false);
  const [emailPreviewData, setEmailPreviewData] = useState<EmailPreviewData | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailForm, setEmailForm] = useState<EmailFormData>({
    toEmail: '',
    ccEmails: [],
    subject: '',
    message: '',
    includePdf: true,
  });

  // Get customer details
  const { data: customer } = useGetOne<Customer>(
    'customers',
    { id: record?.customerId },
    { enabled: !!record?.customerId }
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const handleEdit = () => {
    redirect('edit', 'estimates', record?.id);
  };

  const handleSend = async () => {
    if (!record?.id || !customer) {
      return;
    }

    // Initialize form with customer email and generate preview
    const initialEmailData = {
      toEmail: customer.email || '',
      ccEmails: [],
      subject: `Estimate ${record.billNumber} from Your Company`,
      message: `Please find attached your estimate for ${record.title}.\n\nThank you for your business!`,
      includePdf: true,
    };

    setEmailForm(initialEmailData);
    setEmailDialogOpen(true);

    // Generate preview
    try {
      setIsLoadingPreview(true);
      const preview = await generateEmailPreview(record.id, initialEmailData);
      setEmailPreviewData(preview);
      setEmailForm(prev => ({
        ...prev,
        subject: preview.subject,
      }));
    } catch (error) {
      notify('Failed to generate email preview', { type: 'error' });
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handlePrint = async () => {
    if (!record?.id) {
      return;
    }

    setPrintPreviewOpen(true);

    // Generate PDF preview using the server API
    try {
      setIsLoadingPrintPreview(true);
      const preview = await generateEmailPreview(record.id, {
        toEmail: 'temp@example.com', // Required but not used for PDF preview
        includePdf: true,
      });
      setPrintPreviewData(preview);
    } catch (error) {
      notify('Failed to generate print preview', { type: 'error' });
    } finally {
      setIsLoadingPrintPreview(false);
    }
  };

  const handlePreview = async () => {
    if (!record?.id) {
      return;
    }

    setPreviewOpen(true);

    // Generate preview using the server API
    try {
      setIsLoadingPreviewData(true);
      const preview = await generateEmailPreview(record.id, {
        toEmail: 'temp@example.com', // Required but not used for preview
        includePdf: true,
      });
      setPreviewData(preview);
    } catch (error) {
      notify('Failed to generate preview', { type: 'error' });
    } finally {
      setIsLoadingPreviewData(false);
    }
  };

  const handleActualPrint = () => {
    window.print();
  };

  const handleApprove = async () => {
    if (!record?.id) {
      return;
    }

    try {
      const accessToken = localStorage.getItem('accessToken');
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;

      const response = await fetch(`${baseUrl}/crm/bills/${record.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'x-tenant-code': localStorage.getItem('currentTenant')
            ? JSON.parse(localStorage.getItem('currentTenant')!).code
            : 'default',
        },
        body: JSON.stringify({
          state: 'Approved',
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to approve estimate');
      }

      notify('Estimate approved successfully!', { type: 'success' });
      setApproveDialogOpen(false);

      // Refresh the data to show updated status
      refresh();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to approve estimate';
      notify(errorMessage, { type: 'error' });
    }
  };

  const handleUnapprove = async () => {
    if (!record?.id) {
      return;
    }

    // eslint-disable-next-line no-alert
    if (
      !window.confirm(
        'Are you sure you want to unapprove this estimate? This will change it back to draft status.'
      )
    ) {
      return;
    }

    try {
      const accessToken = localStorage.getItem('accessToken');
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;

      const response = await fetch(`${baseUrl}/crm/bills/${record.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'x-tenant-code': localStorage.getItem('currentTenant')
            ? JSON.parse(localStorage.getItem('currentTenant')!).code
            : 'default',
        },
        body: JSON.stringify({
          state: 'Draft',
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to unapprove estimate');
      }

      notify('Estimate unapproved successfully! Status changed back to draft.', {
        type: 'success',
      });

      // Refresh the data to show updated status
      refresh();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to unapprove estimate';
      notify(errorMessage, { type: 'error' });
    }
  };

  const handleGenerateInvoice = () => {
    setInvoiceWizardOpen(true);
  };

  const handleCreateInvoices = async (invoices: any[]) => {
    const accessToken = localStorage.getItem('accessToken');
    const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;

    try {
      const promises = invoices.map(invoice =>
        fetch(`${baseUrl}/crm/invoices`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'x-tenant-code': localStorage.getItem('currentTenant')
              ? JSON.parse(localStorage.getItem('currentTenant')!).code
              : 'default',
          },
          body: JSON.stringify({
            title: invoice.title,
            description: invoice.description,
            amount: invoice.amount,
            dueDate: invoice.dueDate,
            customerId: record?.customerId,
            estimateId: record?.id,
            status: 'draft',
          }),
        })
      );

      const responses = await Promise.all(promises);
      await Promise.all(responses.map(r => r.json()));

      notify(`Successfully created ${invoices.length} invoice(s)!`, { type: 'success' });
      setInvoiceWizardOpen(false);
    } catch (error) {
      throw new Error('Failed to create invoices');
    }
  };

  const handlePreviewEmail = async () => {
    if (!record?.id) {
      return;
    }

    try {
      setIsLoadingPreview(true);
      const preview = await generateEmailPreview(record.id, emailForm);
      setEmailPreviewData(preview);
      setEmailForm(prev => ({
        ...prev,
        subject: preview.subject,
      }));
    } catch (error) {
      notify('Failed to generate email preview', { type: 'error' });
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleSendEmail = async () => {
    if (!record?.id) {
      return;
    }

    try {
      setIsSendingEmail(true);
      const result = await sendEstimateEmail(record.id, emailForm);

      if (result.sent) {
        notify(`Estimate sent successfully to ${result.to}`, { type: 'success' });
        setEmailDialogOpen(false);
        setEmailPreviewData(null);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send email';
      notify(errorMessage, { type: 'error' });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleEmailFormChange = (
    field: keyof EmailFormData,
    value: string | string[] | boolean
  ) => {
    setEmailForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const addCcEmail = () => {
    setEmailForm(prev => ({
      ...prev,
      ccEmails: [...prev.ccEmails, ''],
    }));
  };

  const updateCcEmail = (index: number, value: string) => {
    setEmailForm(prev => ({
      ...prev,
      ccEmails: prev.ccEmails.map((email, i) => (i === index ? value : email)),
    }));
  };

  const removeCcEmail = (index: number) => {
    setEmailForm(prev => ({
      ...prev,
      ccEmails: prev.ccEmails.filter((_, i) => i !== index),
    }));
  };

  if (!record) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Typography>Loading estimate...</Typography>
      </Box>
    );
  }

  const breadcrumbItems: BreadcrumbItem[] = [
    {
      label: 'Estimates',
      path: '/estimates',
      icon: <MoneyIcon fontSize="small" />,
    },
    {
      label: `Estimate #${record.estimateNumber}`,
      icon: <MoneyIcon fontSize="small" />,
    },
  ];

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <NavigationBreadcrumbs items={breadcrumbItems} />
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background:
            brand?.theme?.headerGradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Stack direction="row" spacing={2} alignItems="center">
                <MoneyIcon sx={{ fontSize: 40, color: 'white' }} />
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {record.title || record.notes || 'Estimate'}
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Estimate #{record.billNumber}
                  </Typography>
                  <StatusChip record={record} />
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleEdit}
                  sx={{ color: 'white', borderColor: 'white' }}
                >
                  Edit
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PreviewIcon />}
                  onClick={handlePreview}
                  sx={{ color: 'white', borderColor: 'white' }}
                >
                  Preview
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Customer Summary */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <PersonIcon color="primary" />
                Customer Summary
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {customer ? (
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {customer.displayName}
                    </Typography>
                    {customer.company && (
                      <Typography variant="body2" color="text.secondary">
                        {customer.company}
                      </Typography>
                    )}
                  </Box>
                  {customer.email && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body2">{customer.email}</Typography>
                    </Box>
                  )}
                  {customer.phone && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Phone
                      </Typography>
                      <Typography variant="body2">{customer.phone}</Typography>
                    </Box>
                  )}
                  <Divider />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Issue Date
                    </Typography>
                    <Typography variant="body2">
                      {record.billDate ? new Date(record.billDate).toLocaleDateString() : 'Not set'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Valid Until
                    </Typography>
                    <Typography variant="body2">
                      {record.validUntil
                        ? new Date(record.validUntil).toLocaleDateString()
                        : 'Not set'}
                    </Typography>
                  </Box>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Loading customer information...
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Estimate Summary */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <BusinessIcon color="primary" />
                Estimate Summary
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {/* Line Items */}
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Service</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: 80 }}>Qty</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Unit Price</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {record.lineItems?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {item.name}
                          </Typography>
                          {item.description && (
                            <Typography variant="caption" color="text.secondary">
                              {item.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>{formatCurrency(item.unitPrice)}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(item.quantity * item.unitPrice)}
                        </TableCell>
                      </TableRow>
                    )) || (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No line items found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Cost Breakdown */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Box sx={{ minWidth: 250 }}>
                  <Stack spacing={1}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography>Subtotal:</Typography>
                      <Typography>{formatCurrency(record.subtotal)}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography>Sales Tax:</Typography>
                      <Typography>{formatCurrency(record.salesTaxAmount)}</Typography>
                    </Box>
                    <Divider />
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Total:
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {formatCurrency(record.totalAmount)}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Box>

              {/* Additional Information */}
              {(record.notes || record.terms) && (
                <Box sx={{ mt: 3 }}>
                  <Divider sx={{ mb: 2 }} />
                  {record.notes && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Internal Notes
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {record.notes}
                      </Typography>
                    </Box>
                  )}
                  {record.terms && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Terms & Conditions
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {record.terms}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Actions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Actions
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<EmailIcon />}
                    onClick={handleSend}
                    sx={{ minHeight: 56 }}
                  >
                    Email to Customer
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<PrintIcon />}
                    onClick={handlePrint}
                    sx={{ minHeight: 56 }}
                  >
                    Print Preview
                  </Button>
                </Grid>
                {!record.isApproved ? (
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<ApproveIcon />}
                      onClick={() => setApproveDialogOpen(true)}
                      disabled={record.state === 'Rejected'}
                      color="success"
                      sx={{ minHeight: 56 }}
                    >
                      Approve
                    </Button>
                  </Grid>
                ) : (
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<ApproveIcon />}
                      onClick={handleUnapprove}
                      color="warning"
                      sx={{ minHeight: 56 }}
                    >
                      Unapprove
                    </Button>
                  </Grid>
                )}
                {record.isApproved && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<InvoiceIcon />}
                      onClick={handleGenerateInvoice}
                      sx={{
                        minHeight: 56,
                        background: brand.theme.buttonGradient,
                        color: 'white',
                        '&:hover': {
                          background: brand.theme.buttonGradient,
                          opacity: 0.9,
                        },
                      }}
                    >
                      Generate Invoice(s)
                    </Button>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Estimate Preview</Typography>
            <Button onClick={() => setPreviewOpen(false)} color="inherit" size="small">
              <CloseIcon />
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {isLoadingPreviewData ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 400,
              }}
            >
              <Stack spacing={2} alignItems="center">
                <CircularProgress size={48} />
                <Typography>Generating preview...</Typography>
              </Stack>
            </Box>
          ) : previewData ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Estimate preview generated successfully. This shows how the document will appear
                  when printed or emailed.
                </Typography>
              </Alert>

              {/* HTML Preview Container */}
              <Box
                sx={{
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: '70vh',
                  backgroundColor: 'white',
                }}
              >
                <div
                  dangerouslySetInnerHTML={{ __html: previewData.html }}
                  style={{
                    padding: '20px',
                    fontFamily: 'Arial, sans-serif',
                    lineHeight: '1.5',
                    color: '#333',
                  }}
                />
              </Box>
            </Box>
          ) : (
            <Alert severity="error">
              <Typography variant="body2">
                Failed to generate preview. Please try again or contact support if the issue
                persists.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          {previewData && (
            <Button
              onClick={handlePrint}
              variant="contained"
              startIcon={<PrintIcon />}
              sx={{
                background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                color: 'white',
              }}
            >
              Print Preview
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)}>
        <DialogTitle>Approve Estimate</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Are you sure you want to approve this estimate? This action will change the status to
            &quot;Approved&quot; and allow invoice generation.
          </Alert>
          <Typography variant="body2">
            <strong>Estimate:</strong> #{record.billNumber} - {record.title}
          </Typography>
          <Typography variant="body2">
            <strong>Customer:</strong> {customer?.displayName}
          </Typography>
          <Typography variant="body2">
            <strong>Total Amount:</strong> {formatCurrency(record.totalAmount)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleApprove} color="success" variant="contained">
            Approve Estimate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Print Preview Dialog */}
      <Dialog
        open={printPreviewOpen}
        onClose={() => setPrintPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Print Preview</Typography>
            <Button onClick={() => setPrintPreviewOpen(false)} color="inherit" size="small">
              <CloseIcon />
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {isLoadingPrintPreview ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 400,
              }}
            >
              <Stack spacing={2} alignItems="center">
                <CircularProgress size={48} />
                <Typography>Generating print preview...</Typography>
              </Stack>
            </Box>
          ) : printPreviewData ? (
            <Box sx={{ textAlign: 'center' }}>
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Print Preview Ready
                </Typography>
                <Typography variant="body2">
                  Your estimate PDF has been generated and is ready to print. The PDF includes:
                </Typography>
              </Alert>

              <Card variant="outlined" sx={{ mb: 3, textAlign: 'left' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Document Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Document:</strong> {printPreviewData.templateData.billNumber}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Customer:</strong> {printPreviewData.templateData.customer.name}
                      </Typography>
                      {printPreviewData.templateData.customer.company && (
                        <Typography variant="body2">
                          <strong>Company:</strong> {printPreviewData.templateData.customer.company}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Total Amount:</strong>{' '}
                        {printPreviewData.templateData.formattedAmount}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Date:</strong> {printPreviewData.templateData.billDate}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Type:</strong>{' '}
                        {printPreviewData.templateData.isEstimate ? 'Estimate' : 'Invoice'}
                      </Typography>
                    </Grid>
                  </Grid>

                  {printPreviewData.templateData.notes && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>Notes:</strong> {printPreviewData.templateData.notes}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>

              <Alert severity="info">
                <Typography variant="body2">
                  The PDF has been generated using your company template and is ready for printing.
                  Click the &quot;Print PDF&quot; button below to open the print dialog.
                </Typography>
              </Alert>
            </Box>
          ) : (
            <Alert severity="error">
              <Typography variant="body2">
                Failed to generate print preview. Please try again or contact support if the issue
                persists.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrintPreviewOpen(false)}>Close</Button>
          <Button
            onClick={handleActualPrint}
            variant="contained"
            startIcon={<PrintIcon />}
            disabled={!printPreviewData}
            sx={{
              background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
              color: 'white',
            }}
          >
            Print PDF
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Send Estimate via Email</Typography>
            <Button onClick={() => setEmailDialogOpen(false)} color="inherit" size="small">
              <CloseIcon />
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {/* Email Form */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Email Details
                </Typography>

                {/* To Email */}
                <MuiTextField
                  fullWidth
                  label="To Email"
                  type="email"
                  value={emailForm.toEmail}
                  onChange={e => handleEmailFormChange('toEmail', e.target.value)}
                  required
                  sx={{ mb: 2 }}
                  error={!emailForm.toEmail}
                  helperText={!emailForm.toEmail ? 'Email address is required' : ''}
                />

                {/* CC Emails */}
                <Box sx={{ mb: 2 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="subtitle2">CC Emails (Optional)</Typography>
                    <Button size="small" onClick={addCcEmail}>
                      Add CC
                    </Button>
                  </Stack>
                  {emailForm.ccEmails.map((email, index) => (
                    <Stack key={index} direction="row" spacing={1} sx={{ mb: 1 }}>
                      <MuiTextField
                        fullWidth
                        size="small"
                        type="email"
                        placeholder="cc@example.com"
                        value={email}
                        onChange={e => updateCcEmail(index, e.target.value)}
                      />
                      <Button size="small" color="error" onClick={() => removeCcEmail(index)}>
                        Remove
                      </Button>
                    </Stack>
                  ))}
                </Box>

                {/* Subject */}
                <MuiTextField
                  fullWidth
                  label="Subject"
                  value={emailForm.subject}
                  onChange={e => handleEmailFormChange('subject', e.target.value)}
                  sx={{ mb: 2 }}
                />

                {/* Message */}
                <MuiTextField
                  fullWidth
                  label="Message"
                  multiline
                  rows={4}
                  value={emailForm.message}
                  onChange={e => handleEmailFormChange('message', e.target.value)}
                  placeholder="Enter your message here..."
                  sx={{ mb: 2 }}
                />

                {/* Include PDF */}
                <FormControl>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2">Include PDF Attachment:</Typography>
                    <Chip
                      label={emailForm.includePdf ? 'Yes' : 'No'}
                      color={emailForm.includePdf ? 'success' : 'default'}
                      size="small"
                    />
                  </Stack>
                </FormControl>
              </CardContent>
            </Card>

            {/* Email Preview */}
            {emailPreviewData && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Email Preview
                  </Typography>
                  {isLoadingPreview ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Subject: {emailPreviewData.subject}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        To: {emailForm.toEmail}
                      </Typography>
                      {emailForm.ccEmails.length > 0 && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          CC: {emailForm.ccEmails.filter(email => email.trim()).join(', ')}
                        </Typography>
                      )}
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                        {emailForm.message}
                      </Typography>
                      {emailPreviewData.templateData.customMessage && (
                        <Typography variant="body2" color="text.secondary">
                          {emailPreviewData.templateData.customMessage}
                        </Typography>
                      )}
                      <Alert severity="info" sx={{ mt: 2 }}>
                        PDF attachment will be included: {emailPreviewData.templateData.billNumber}
                      </Alert>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Error Handling */}
            {!emailPreviewData && !isLoadingPreview && emailForm.toEmail && (
              <Alert severity="info">
                Click &quot;Preview Email&quot; to see how the email will look before sending.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handlePreviewEmail}
            disabled={!emailForm.toEmail || isLoadingPreview}
            startIcon={isLoadingPreview ? <CircularProgress size={16} /> : <PreviewIcon />}
          >
            Preview Email
          </Button>
          <Button
            onClick={handleSendEmail}
            variant="contained"
            disabled={!emailForm.toEmail || !emailPreviewData || isSendingEmail}
            startIcon={isSendingEmail ? <CircularProgress size={16} /> : <SendIcon />}
            sx={{
              background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
              color: 'white',
            }}
          >
            Send Email
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Generation Wizard */}
      {invoiceWizardOpen && record && (
        <Dialog
          open={invoiceWizardOpen}
          onClose={() => setInvoiceWizardOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <InvoiceGenerationWizard
            estimate={{
              id: record.id,
              estimateNumber: record.billNumber,
              customerId: record.customerId,
              customer: customer
                ? {
                    id: customer.id,
                    displayName: customer.displayName,
                    email: customer.email,
                  }
                : undefined,
              title: record.title || record.notes || 'Estimate',
              totalAmount: record.totalAmount,
              subtotal: record.subtotal,
              taxAmount: record.taxAmount,
              taxRate: record.taxRate,
              lineItems: record.lineItems,
            }}
            onGenerate={handleCreateInvoices}
            onClose={() => setInvoiceWizardOpen(false)}
          />
        </Dialog>
      )}
    </Box>
  );
};

export const EstimateShow = () => (
  <Show actions={<ShowActions />}>
    <EstimateShowContent />
  </Show>
);
